import type { Block } from '@/types';
import { uboTheme } from '@/theme/uboTheme';
import PageHeader from './PageHeader';
import { useQuotePageBrand } from '@/store/quoteClientLogoContext';

export interface FooterBlockContent {
  headline?: string;
  theme?: 'ubo' | 'ubx';
}

interface FooterBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
}

function FooterBlock({ block, isEditable = false, onUpdate }: FooterBlockProps) {
  const content = block.content as FooterBlockContent;
  const quoteBrand = useQuotePageBrand();
  const theme = content.theme ?? quoteBrand;

  const bgColor = theme === 'ubx' ? uboTheme.colors.ubx.primary : uboTheme.colors.primary;
  const logoSrc = theme === 'ubx' ? '/ubo/ubx-logo.svg' : '/ubo/logo.svg';
  const bgSrc = theme === 'ubx' ? '/ubo/ubx-background-1.svg' : '/ubo/background-1.svg';

  const isUbx = theme === 'ubx';
  const defaultHeadline = isUbx
    ? 'GEEN FRUSTRATIE\nMET JOUW\nHUBSPOT Implementatie'
    : 'DURF\nVOOROP\nTE LOPEN';
  const headlineText = content.headline || defaultHeadline;
  /** Langere UBX-koppen (meer woorden/regels) vragen een hoger tekstvak dan UBO. */
  const headlineEditorHeightPx = isUbx ? 520 : 300;
  const headlineFontPx = isUbx ? 100 : 120;

  if (isEditable) {
    return (
      <div
        data-full-bleed-surface
        className={`w-full h-full flex-1 relative overflow-hidden flex flex-col text-white ${isUbx ? 'min-h-0' : 'justify-between'}`}
        style={{ backgroundColor: bgColor, padding: 50 }}
      >
        <img
          src={bgSrc}
          alt="background"
          data-full-bleed-artwork
          className="absolute left-0 top-0 w-full h-full object-cover"
        />

        <div className="relative z-10 shrink-0">
          <PageHeader
            agencyLogoUrl={logoSrc}
            brand={theme === 'ubx' ? 'ubx' : 'ubo'}
            variant="dark"
          />
        </div>

        <div
          className={`z-10 w-full min-w-0 ${isUbx ? 'flex flex-1 flex-col justify-end min-h-0' : 'mt-auto'}`}
        >
          <textarea
            value={headlineText}
            onChange={(e) => onUpdate?.({ content: { ...content, headline: e.target.value } })}
            className={`w-full bg-transparent border-none outline-none text-white ${isUbx ? 'resize-y min-h-[320px]' : 'resize-none'}`}
            style={{
              fontSize: `${headlineFontPx}px`,
              fontWeight: 900,
              textTransform: 'uppercase',
              fontFamily: uboTheme.fonts.primary,
              lineHeight: 0.85,
              height: headlineEditorHeightPx,
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      data-full-bleed-surface
      className={`w-full h-full flex-1 relative overflow-hidden flex flex-col text-white ${isUbx ? 'min-h-0' : 'justify-between'}`}
      style={{ backgroundColor: bgColor, padding: 50 }}
    >
      <img
        src={bgSrc}
        alt="background"
        data-full-bleed-artwork
        className="absolute left-0 top-0 w-full h-full object-cover"
      />

      <div className="relative z-10 shrink-0">
        <PageHeader
          agencyLogoUrl={logoSrc}
          brand={theme === 'ubx' ? 'ubx' : 'ubo'}
          variant="dark"
        />
      </div>

      <div
        className={`z-10 w-full min-w-0 ${isUbx ? 'flex flex-1 flex-col justify-end min-h-0 overflow-visible' : 'mt-auto overflow-hidden'}`}
        style={{
          fontSize: headlineFontPx,
          fontWeight: 900,
          textTransform: 'uppercase',
          fontFamily: uboTheme.fonts.primary,
          lineHeight: 0.85,
          wordBreak: 'break-word',
        }}
      >
        <span className="whitespace-pre-line block w-full">
          {headlineText}
        </span>
      </div>
    </div>
  );
}

export default FooterBlock;
